import { motion } from "framer-motion";
import { Article } from "../types";
import { Clock, User, Calendar, ArrowRight } from "lucide-react";
import { useLocale } from "../context/LocaleContext";

interface ArticleCardProps {
  article: Article;
  index: number;
  onClick: () => void;
  readArticleText?: string;
  minReadText?: string;
}

export function ArticleCard({
  article,
  index,
  onClick,
  readArticleText = "Read Article",
  minReadText = "min read",
}: ArticleCardProps) {
  const { locale } = useLocale();

  // Add null check for article
  if (!article) {
    return null;
  }

  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      whileHover={{ y: -8 }}
      onClick={onClick}
      className="relative bg-gray-900/50 backdrop-blur-sm border border-red-900/20 rounded-xl overflow-hidden hover:border-red-600/50 transition-all cursor-pointer group"
    >
      {/* Cover image if available */}
      {article?.cover?.url && (
        <div className="relative h-48 overflow-hidden bg-gray-800">
          <img
            src={`${
              process.env.REACT_APP_STRAPI_URL ||
              "https://devops.caglayagmur.dev/api"
            }${article.cover.url}`}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900 to-transparent" />
        </div>
      )}

      {/* Content */}
      <div className="p-6">
        {/* Meta information */}
        <div className="flex items-center gap-3 text-sm text-gray-400 mb-4 flex-wrap">
          <div className="flex items-center gap-1.5">
            <User className="w-4 h-4 text-red-600" />
            <span>{article?.author || "Unknown"}</span>
          </div>
          <span className="text-gray-600">•</span>
          <div className="flex items-center gap-1.5">
            <Clock className="w-4 h-4 text-red-600" />
            <span>
              {article?.read_time || 5} {minReadText}
            </span>
          </div>
          {article?.publishedAt && (
            <>
              <span className="text-gray-600">•</span>
              <div className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4 text-red-600" />
                <span>
                  {new Date(article.publishedAt).toLocaleDateString(
                    locale === "tr-TR" ? "tr-TR" : "en-US",
                    {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    }
                  )}
                </span>
              </div>
            </>
          )}
        </div>

        {/* Category badge if available */}
        {article?.category?.name && (
          <div className="mb-3">
            <span className="inline-block px-3 py-1 text-xs font-medium bg-red-600/10 text-red-500 border border-red-600/20 rounded-full">
              {article.category.name}
            </span>
          </div>
        )}

        {/* Title */}
        <h2 className="text-xl font-bold mb-3 group-hover:text-red-600 transition-colors line-clamp-2">
          {article?.title || "Untitled"}
        </h2>

        {/* Excerpt */}
        <p className="text-gray-400 leading-relaxed line-clamp-3 mb-4">
          {article?.excerpt || "No description available."}
        </p>

        {/* Read more indicator */}
        <div className="flex items-center gap-2 text-red-600 font-medium text-sm group-hover:gap-3 transition-all">
          <span>{readArticleText}</span>
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </div>
      </div>

      {/* Decorative corner */}
      <div className="absolute top-0 right-0 w-20 h-20 bg-red-600/5 rounded-bl-full group-hover:bg-red-600/10 transition-all" />
    </motion.article>
  );
}
